'use client';

import { ListingIntent } from '@vs-shop/engine/listing/atom/makeListingAtoms';
import type { ListingBinding } from '@vs-shop/engine/listing/atom/makeListingAtoms';

import { Array, HashSet, Match, Option, Predicate } from 'effect';
import { intentHandlers, type IntentSource } from '@vs-shop/headless/intentHandlers';

import { useAtom, useAtomSet, useAtomValue } from '@effect/atom-react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import {
  FilterDrawerApply,
  FilterDrawerBar,
  FilterDrawerBody,
  FilterDrawerClearAll,
  FilterDrawerClose,
  FilterDrawerCount,
  FilterDrawerFooter,
  FilterDrawerLabel,
  FilterDrawerPanel,
  FilterDrawerRail,
  FilterDrawerTitle,
  FilterDrawerTrigger,
} from '@vs-shop/commerce-ui/filterDrawer';
import { ListingChange } from '@vs-shop/engine/capability/productListing/listingSelection';
import type { Facet } from '@vs-shop/engine/capability/productListing/facetSet';
import type { ListingScopeKey } from '@vs-shop/engine/capability/productListing/listingScope';
import { facetKeyId } from '@vs-shop/engine/capability/productListing/facetKey';
import {
  FilterRailColorOptions,
  FilterRailFacet,
  FilterRailOptions,
  FilterRailTitle,
} from '@vs-shop/commerce-ui/filterRail';
import { Heading } from '@vs-shop/ui/heading';
import { Drawer } from '@vs-shop/ui/drawer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@vs-shop/ui/accordion';
import { Loader } from '@vs-shop/ui/loader';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { ListFilter } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { rcprofiListingJourney } from '../../../effect/listing/listingJourney';
import { isValidColorCode } from '../../../config/colorsForProductFilters';
import { colorFilterCode } from '../../../shared/config/productFilters';
import FilterChoiceList from '../filter/FilterChoiceList';
import { FilterColorCell } from '../../server/filter/FilterColorCell';
import { FilterRow } from '../../server/filter/FilterRow';
import { listingFacetPresentation } from './listingFacetPresentation';
import { ListingRangeFilter } from './ListingRangeFilter';
import type { ListingFilterAttribute } from './listingFilterAttribute';

type Props = { readonly scopeKey: ListingScopeKey } & (
  | { readonly search: true }
  | { readonly search: false; readonly attributes: ReadonlyArray<ListingFilterAttribute> }
);

const pendingCount = <Loader layout="inline" size={20} color="currentColor" />;

type TermsFacet = Extract<Facet, { readonly _tag: 'Terms' }>;

const TermsFilter = ({
  facet,
  selected,
  onToggle,
  onIntent,
  onCancelIntent,
  initialVisibleCount,
}: {
  readonly facet: TermsFacet;
  readonly selected: HashSet.HashSet<string>;
  readonly onToggle: (value: string) => void;
  readonly onIntent: (value: string, source: IntentSource) => void;
  readonly onCancelIntent: (source: IntentSource) => void;
  readonly initialVisibleCount: number;
}) => {
  const intl = useIntl();
  const attributeCode = Option.some(facet.key).pipe(
    Option.filter((key) => Predicate.isTagged(key, 'Attribute')),
    Option.map((key) => key.code)
  );
  const isColor = Option.exists(attributeCode, (code) => code === colorFilterCode);
  const options = Match.value(isColor).pipe(
    Match.when(true, () => Array.filter(facet.options, (option) => isValidColorCode(option.value))),
    Match.when(false, () => Array.fromIterable(facet.options)),
    Match.exhaustive
  );
  const disabled = (option: TermsFacet['options'][number]) =>
    option.count === 0 && HashSet.size(selected) === 0;
  const optionIntentHandlers = (option: TermsFacet['options'][number]) =>
    Match.value(disabled(option)).pipe(
      Match.when(true, () => ({})),
      Match.when(false, () =>
        intentHandlers({
          start: (source) => onIntent(option.value, source),
          cancel: onCancelIntent,
        })
      ),
      Match.exhaustive
    );
  if (Array.isReadonlyArrayEmpty(options)) return null;
  if (isColor) {
    return (
      <FilterRailFacet>
        <FilterRailColorOptions>
          {Array.map(options, (option) =>
            isValidColorCode(option.value) ? (
              <FilterColorCell
                key={option.value}
                id={`checkbox-${Option.getOrNull(attributeCode)}-${option.value}`}
                colorCode={option.value}
                label={option.label}
                count={Option.getOrUndefined(Option.fromNullishOr(option.count))}
                checked={HashSet.has(selected, option.value)}
                disabled={disabled(option)}
                onCheckedChange={() => onToggle(option.value)}
                {...optionIntentHandlers(option)}
              />
            ) : null
          )}
        </FilterRailColorOptions>
      </FilterRailFacet>
    );
  }

  const row = (option: TermsFacet['options'][number]) => (
    <FilterRow
      key={option.value}
      id={`checkbox-${facetKeyId(facet.key)}-${option.value}`}
      label={option.label}
      count={Option.getOrUndefined(Option.fromNullishOr(option.count))}
      checked={HashSet.has(selected, option.value)}
      disabled={disabled(option)}
      onCheckedChange={() => onToggle(option.value)}
      {...optionIntentHandlers(option)}
    />
  );
  if (Predicate.isTagged(facet.key, 'StockLocation') || facet.valueType === 'boolean') {
    return (
      <FilterRailFacet>
        <FilterRailOptions>{Array.map(options, row)}</FilterRailOptions>
      </FilterRailFacet>
    );
  }

  const search = Match.value(facet.key).pipe(
    Match.tag('Brand', () => ({
      placeholder: intl.formatMessage({
        id: 'component.server.filter.filtersAccordionContentBrands.searchPlaceholder',
        defaultMessage: 'Hledat značky...',
      }),
      settings: {
        searchThreshold: 0.3,
        searchKeys: [
          { name: 'label', weight: 2 },
          { name: 'searchText', weight: 1 },
        ],
      },
    })),
    Match.tag('Taxon', () => ({
      placeholder: intl.formatMessage({
        id: 'component.server.filter.searchFiltersAccordionContentTaxons.searchPlaceholder',
        defaultMessage: 'Hledat kategorie...',
      }),
      settings: { searchThreshold: 0.3 },
    })),
    Match.tag('Attribute', () => ({
      placeholder: intl.formatMessage({
        id: 'component.server.filter.filtersAccordionContentMultipleChoice.searchPlaceholder',
        defaultMessage: 'Hledat...',
      }),
      settings: {
        searchKeys: [
          { name: 'label', weight: 2 },
          { name: 'value', weight: 1 },
        ],
      },
    })),
    Match.tag('Price', 'StockLocation', () => ({
      placeholder: intl.formatMessage({
        id: 'component.server.filter.filtersAccordionContentMultipleChoice.searchPlaceholder',
        defaultMessage: 'Hledat...',
      }),
      settings: {},
    })),
    Match.exhaustive
  );

  return (
    <FilterChoiceList
      items={options}
      getKeyAction={(option) => option.value}
      searchPlaceholder={search.placeholder}
      {...search.settings}
      initialVisibleCount={initialVisibleCount}
      renderItemAction={row}
    />
  );
};

/** Shows the dragged bounds at once; the engine commits them after a pause. */
const RangeFacet = ({
  facet,
  range,
}: {
  readonly facet: Extract<Facet, { readonly _tag: 'Range' }>;
  readonly range: ListingBinding<never>['range'];
}) => {
  const [bounds, setBounds] = useAtom(range(facet.key));
  return (
    <ListingRangeFilter
      facet={facet}
      selected={{ min: Option.getOrNull(bounds.min), max: Option.getOrNull(bounds.max) }}
      onChange={(min, max) =>
        setBounds({ min: Option.fromNullishOr(min), max: Option.fromNullishOr(max) })
      }
    />
  );
};

export const ListingFilters = (props: Props) => {
  const { scopeKey } = props;
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setHydrated(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  const binding = rcprofiListingJourney.bind(scopeKey);
  const state = useAtomValue(binding.state);
  const current = useAtomValue(binding.current);
  const productCount = useAtomValue(binding.productCount);
  const change = useAtomSet(binding.change, { mode: 'value' });
  const prefetch = useAtomSet(binding.prefetch);
  const { rail } = listingFacetPresentation({ state, current, intl, layout: props });
  const total = AsyncResult.value(productCount);
  const title = intl.formatMessage({
    id: 'component.server.page.category.filter.categoryFilters.filtering',
    defaultMessage: 'Filtrace',
  });
  const drawerTitle = intl.formatMessage({
    id: 'filters.mobile.drawer.title',
    defaultMessage: 'Filtrování',
  });

  const defaultOpen = Array.map(
    Array.filter(rail, (facet) => facet.open),
    (facet) => facet.id
  );
  const accordion = (
    <Accordion key={scopeKey} multiple defaultValue={defaultOpen}>
      {Array.map(rail, (data) => {
        return (
          <AccordionItem key={data.id} value={data.id} variant="plain">
            <AccordionTrigger variant="plain">{data.title}</AccordionTrigger>
            <AccordionContent>
              {Match.value(data).pipe(
                Match.tag('Terms', ({ facet, selected, changeForOption, initialVisibleCount }) => {
                  return (
                    <TermsFilter
                      facet={facet}
                      selected={selected}
                      onToggle={(value) => change(changeForOption(value))}
                      onIntent={(value, source) =>
                        prefetch(ListingIntent.Start({ source, change: changeForOption(value) }))
                      }
                      onCancelIntent={(source) => prefetch(ListingIntent.Cancel({ source }))}
                      initialVisibleCount={initialVisibleCount}
                    />
                  );
                }),
                Match.tag('Range', ({ facet }) => (
                  <RangeFacet facet={facet} range={binding.range} />
                )),
                Match.exhaustive
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );

  return (
    <div data-slot="listing-filters" data-hydrated={hydrated} aria-busy={current.waiting}>
      <div data-slot="listing-filters-desktop" className="hidden tab-lg:flex tab-lg:flex-col">
        <FilterRailTitle render={<Heading level={2} />}>{title}</FilterRailTitle>
        {accordion}
      </div>
      <div data-slot="listing-filters-mobile" className="tab-lg:hidden">
        <Drawer open={open} onOpenChange={setOpen} direction="bottom">
          <FilterDrawerTrigger onClick={() => setOpen(true)}>
            <FilterDrawerLabel>
              {intl.formatMessage({
                id: 'filters.mobile.drawer.button',
                defaultMessage: 'FILTROVAT',
              })}
            </FilterDrawerLabel>
            <ListFilter />
          </FilterDrawerTrigger>
          <FilterDrawerPanel aria-describedby={undefined} data-slot="filters-drawer">
            <FilterDrawerBody>
              <FilterDrawerBar>
                <FilterDrawerTitle>{drawerTitle}</FilterDrawerTitle>
                <FilterDrawerClose
                  data-slot="filters-drawer-close"
                  aria-label={intl.formatMessage({ id: 'common.close', defaultMessage: 'Zavřít' })}
                >
                  <XMarkIcon />
                </FilterDrawerClose>
              </FilterDrawerBar>
              <FilterDrawerRail>{accordion}</FilterDrawerRail>
            </FilterDrawerBody>
            <FilterDrawerFooter>
              <FilterDrawerApply data-slot="filters-drawer-apply" onClick={() => setOpen(false)}>
                <FilterDrawerLabel>
                  {intl.formatMessage({
                    id: 'filters.mobile.drawer.footer.submit',
                    defaultMessage: 'ZOBRAZIT PRODUKTY',
                  })}
                </FilterDrawerLabel>
                <FilterDrawerCount>
                  {Option.match(total, {
                    onNone: () => pendingCount,
                    onSome: (count) => count,
                  })}
                </FilterDrawerCount>
              </FilterDrawerApply>
              <FilterDrawerClearAll
                data-slot="filters-drawer-reset"
                onClick={() => {
                  change(ListingChange.cases.ClearAll.make({}));
                  setOpen(false);
                }}
              >
                {intl.formatMessage({
                  id: 'filters.mobile.drawer.footer.reset',
                  defaultMessage: 'VYMAZAT',
                })}
              </FilterDrawerClearAll>
            </FilterDrawerFooter>
          </FilterDrawerPanel>
        </Drawer>
      </div>
    </div>
  );
};
